import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigation } from "@/components/ui/navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronLeft, ChevronRight, ArrowLeft, Check, X, Info, Eraser, Flag } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useAnalytics } from "@/hooks/useAnalytics";
import { fetchSubjectOutline } from "@/lib/api/subjects";
import { LatexText } from "@/components/ui/latex-text";
import {
  completePracticeSession,
  createPracticeSession,
  fetchAnsweredQuestionIds,
  fetchRandomQuestions,
  listFlaggedQuestionIds,
  recordQuestionAttempts,
  setFlaggedQuestion,
  type PracticeQuestion,
} from "@/lib/api/practice";

type OutlineUnit = Awaited<ReturnType<typeof fetchSubjectOutline>>["units"][number];
type OutlineTopic = OutlineUnit["topics"][number];

type AnswerStatus = "unanswered" | "correct" | "incorrect";

const DIFFICULTY_LABELS: Record<string, string> = {
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
  all: "All",
  unknown: "Unknown",
};

const EMPTY_QUESTIONS: PracticeQuestion[] = [];

export default function PracticeTest() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const { trackPracticeSessionStarted, trackQuestionAnswered, trackQuestionFlagged, trackQuestionUnflagged } = useAnalytics();

  const rawUnitIds = params.getAll("unitId");
  const rawTopicSlugs = params.getAll("topicSlug");
  const difficultyParam = (params.get("difficulty") ?? "all").toLowerCase();
  const difficulty = ["easy", "medium", "hard", "all"].includes(difficultyParam) ? difficultyParam : "all";
  const count = Number.parseInt(params.get("count") || "20", 10);

  const outlineQuery = useQuery({
    queryKey: ["subject-outline", slug],
    queryFn: () => fetchSubjectOutline(slug!),
    enabled: Boolean(slug),
  });

  const subject = outlineQuery.data?.subject ?? null;
  const units = useMemo(() => outlineQuery.data?.units ?? [], [outlineQuery.data?.units]);

  const topicLookup = useMemo(() => {
    const map = new Map<string, OutlineTopic>();
    units.forEach((unit) => {
      unit.topics.forEach((topic) => {
        if (topic.slug) {
          map.set(topic.slug, topic);
        }
      });
    });
    return map;
  }, [units]);

  const unitLookup = useMemo(() => {
    const map = new Map<string, OutlineUnit>();
    units.forEach((unit) => map.set(unit.id, unit));
    return map;
  }, [units]);

  const effectiveUnitIds = useMemo(() => {
    const fromParams = rawUnitIds.filter(Boolean);
    if (fromParams.length) {
      return Array.from(new Set(fromParams));
    }
    const derived = new Set<string>();
    rawTopicSlugs.forEach((slugValue) => {
      const topic = topicLookup.get(slugValue);
      if (topic?.unit_id) derived.add(topic.unit_id);
    });
    if (derived.size) {
      return Array.from(derived);
    }
    if (units.length) {
      return [units[0].id];
    }
    return [];
  }, [rawUnitIds, rawTopicSlugs, topicLookup, units]);

  const selectedTopicIds = useMemo(() => {
    if (!rawTopicSlugs.length) return [];
    const set = new Set<string>();
    rawTopicSlugs.forEach((slugValue) => {
      const topic = topicLookup.get(slugValue);
      if (topic?.id) set.add(topic.id);
    });
    return Array.from(set);
  }, [rawTopicSlugs, topicLookup]);

  const answeredQuery = useQuery({
    queryKey: ["answered-question-ids", user?.id, subject?.id],
    queryFn: () => fetchAnsweredQuestionIds(user!.id, subject!.id),
    enabled: Boolean(user?.id && subject?.id),
  });

  const flaggedQuery = useQuery({
    queryKey: ["flagged-question-ids", user?.id, subject?.id],
    queryFn: () => listFlaggedQuestionIds(user!.id, subject!.id),
    enabled: Boolean(user?.id && subject?.id),
  });

  const answeredIds = answeredQuery.data ?? [];
  const unitKey = [...effectiveUnitIds].sort().join("-");
  const topicKey = [...selectedTopicIds].sort().join("-");
  const answeredKey = [...answeredIds].sort().join("-");

  // Stable signature for the current test configuration
  const signature = useMemo(
    () => [slug, unitKey, topicKey, difficulty, count].join("|"),
    [slug, unitKey, topicKey, difficulty, count],
  );

  // Persist/restore session + questions so the test does not change on refresh or tab switches.
  const draftKey = useMemo(
    () => (user?.id && slug ? `practice-draft:${user.id}:${slug}` : null),
    [user?.id, slug],
  );

  // Read any matching draft synchronously (by signature) so we can block fetch/creation on first render
  const initialDraft = useMemo(() => {
    if (!draftKey) return null;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as {
        signature?: string;
        sessionId?: string | null;
        questions?: PracticeQuestion[] | null;
        selections?: Record<string, number | undefined>;
        checked?: Record<string, AnswerStatus>;
        timings?: Record<string, number>;
        cursor?: number;
        eliminated?: Record<string, number[]>;
        flagged?: Record<string, boolean>;
      } | null;
      if (!parsed || parsed.signature !== signature || !parsed.questions?.length) return null;
      return parsed;
    } catch {
      return null;
    }
  }, [draftKey, signature]);

  const [persistedQuestions, setPersistedQuestions] = useState<PracticeQuestion[] | null>(initialDraft?.questions ?? null);
  const restoringRef = useRef(false);

  // Synchronously detect an existing draft on disk to avoid any race that could refetch/regen
  const hasDraftOnDisk = Boolean(initialDraft);

  const questionsQuery = useQuery({
    queryKey: ["practice-questions", slug, unitKey, topicKey, difficulty, count, answeredKey],
    queryFn: () =>
      fetchRandomQuestions({
        subjectSlug: slug!,
        unitIds: effectiveUnitIds,
        topicIds: selectedTopicIds,
        difficulty,
        count,
        excludeQuestionIds: answeredIds,
      }),
    // Do not fetch if we already have a saved draft on disk or restored in memory
    enabled: Boolean(slug && effectiveUnitIds.length && !answeredQuery.isLoading && !persistedQuestions && !hasDraftOnDisk),
    refetchOnWindowFocus: false,
  });

  const questions: PracticeQuestion[] = (persistedQuestions ?? questionsQuery.data) ?? EMPTY_QUESTIONS;
  const [cursor, setCursor] = useState(0);
  const [selections, setSelections] = useState<Record<string, number | undefined>>({});
  const [checked, setChecked] = useState<Record<string, AnswerStatus>>({});
  const [timings, setTimings] = useState<Record<string, number>>({});
  const [startTick, setStartTick] = useState<number | null>(null);
  const [eliminationMode, setEliminationMode] = useState(false);
  const [eliminated, setEliminated] = useState<Record<string, Set<number>>>({});
  const [flagged, setFlagged] = useState<Record<string, boolean>>({});
  const [sessionId, setSessionId] = useState<string | null>(initialDraft?.sessionId ?? null);
  const sessionKeyRef = useRef<string | null>(null);
  const [finishing, setFinishing] = useState(false);

  useEffect(() => {
    // Prefer the pre-read initialDraft so we avoid a race with fetching
    if (initialDraft) {
      restoringRef.current = true;
      if (initialDraft.sessionId) setSessionId(initialDraft.sessionId);
      if (initialDraft.questions?.length) setPersistedQuestions(initialDraft.questions);
      setSelections(initialDraft.selections ?? {});
      setChecked(initialDraft.checked ?? {});
      setTimings(initialDraft.timings ?? {});
      setCursor(typeof initialDraft.cursor === "number" ? initialDraft.cursor : 0);
      setEliminated(() => {
        const out: Record<string, Set<number>> = {};
        const src = initialDraft.eliminated ?? {};
        Object.keys(src).forEach((qid) => (out[qid] = new Set(src[qid] ?? [])));
        return out;
      });
      setFlagged(initialDraft.flagged ?? {});
      setStartTick(performance.now());
      return;
    }
    if (!draftKey) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        signature?: string;
        sessionId?: string | null;
        questions?: PracticeQuestion[] | null;
        selections?: Record<string, number | undefined>;
        checked?: Record<string, AnswerStatus>;
        timings?: Record<string, number>;
        cursor?: number;
        eliminated?: Record<string, number[]>;
        flagged?: Record<string, boolean>;
      } | null;
      if (!parsed || parsed.signature !== signature) return;
      restoringRef.current = true;
      if (parsed.sessionId) setSessionId(parsed.sessionId);
      if (parsed.questions?.length) setPersistedQuestions(parsed.questions);
      setSelections(parsed.selections ?? {});
      setChecked(parsed.checked ?? {});
      setTimings(parsed.timings ?? {});
      setCursor(typeof parsed.cursor === "number" ? parsed.cursor : 0);
      setEliminated(() => {
        const out: Record<string, Set<number>> = {};
        const src = parsed.eliminated ?? {};
        Object.keys(src).forEach((qid) => (out[qid] = new Set(src[qid] ?? [])));
        return out;
      });
      setFlagged(parsed.flagged ?? {});
      setStartTick(performance.now());
    } catch (e) {
      console.warn("Failed to restore practice draft (late path)", e);
    }
     
  }, [draftKey, signature, initialDraft]);

  // Persist draft whenever key state changes.
  useEffect(() => {
    if (!draftKey) return;
    try {
      const eliminatedArrays: Record<string, number[]> = {};
      Object.keys(eliminated).forEach((qid) => (eliminatedArrays[qid] = Array.from(eliminated[qid] ?? [])));
      const payload = {
        signature,
        sessionId,
        questions: persistedQuestions ?? questionsQuery.data ?? null,
        selections,
        checked,
        timings,
        cursor,
        eliminated: eliminatedArrays,
        flagged,
        config: {
          unitIds: effectiveUnitIds,
          topicSlugs: rawTopicSlugs,
          difficulty,
          count,
        },
        updatedAt: Date.now(),
      };
      localStorage.setItem(draftKey, JSON.stringify(payload));
    } catch (e) {
      // ignore storage errors
    }
  }, [
    draftKey,
    signature,
    sessionId,
    persistedQuestions,
    questionsQuery.data,
    selections,
    checked,
    timings,
    cursor,
    eliminated,
    flagged,
    effectiveUnitIds,
    selectedTopicIds,
    rawTopicSlugs,
    difficulty,
    count,
  ]);

  const createSessionMutation = useMutation({
    mutationFn: () =>
      createPracticeSession({
        userId: user!.id,
        subjectSlug: slug!,
        unitIds: effectiveUnitIds,
        topicIds: selectedTopicIds,
        difficulty,
        count,
      }),
    onSuccess: (data) => {
      setSessionId(data.id);
      // Track practice session started
      trackPracticeSessionStarted({
        subjectSlug: slug!,
        unitIds: effectiveUnitIds,
        topicIds: selectedTopicIds.length > 0 ? selectedTopicIds : undefined,
        difficulty,
        questionCount: count,
      });
    },
    onError: (error: unknown) => {
      console.error(error);
      const message = error instanceof Error ? error.message : "Unknown error";
      toast({ title: "Cannot start session", description: message, variant: "destructive" });
    },
  });

  useEffect(() => {
    if (!user || !slug || !effectiveUnitIds.length) return;
    if (sessionId) return;
    // If a saved draft exists (on disk or restored), don't create a new session
    if ((persistedQuestions && persistedQuestions.length) || hasDraftOnDisk) return;
    const key = [user.id, slug, unitKey, topicKey, difficulty, count].join("|");
    if (sessionKeyRef.current === key) return;
    sessionKeyRef.current = key;
    createSessionMutation.mutate();
  }, [user, slug, effectiveUnitIds, unitKey, topicKey, difficulty, count, sessionId, createSessionMutation, persistedQuestions, hasDraftOnDisk]);

  useEffect(() => {
    if (!questions.length) return;
    // Skip default reset when restoring from a saved draft.
    if (restoringRef.current) {
      restoringRef.current = false;
      return;
    }
    setCursor(0);
    setSelections({});
    setChecked({});
    setTimings({});
    setStartTick(performance.now());
    setEliminated({});
    setEliminationMode(false);
  }, [questions.length]);

  useEffect(() => {
    const flaggedIds = new Set(flaggedQuery.data ?? []);
    setFlagged((prev) => {
      const next: Record<string, boolean> = {};
      questions.forEach((q) => {
        next[q.id] = flaggedIds.has(q.id) || Boolean(prev[q.id]);
      });
      return next;
    });
  }, [flaggedQuery.data, questions]);

  useEffect(() => {
    setStartTick(performance.now());
     
  }, [cursor]);

  const toggleFlagMutation = useMutation({
    mutationFn: ({ questionId, next }: { questionId: string; next: boolean }) =>
      setFlaggedQuestion(user!.id, questionId, next),
    onError: (error: unknown, variables) => {
      setFlagged((prev) => ({ ...prev, [variables.questionId]: !variables.next }));
      const message = error instanceof Error ? error.message : "Unknown error";
      toast({ title: "Flag update failed", description: message, variant: "destructive" });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["flagged-question-ids", user?.id, subject?.id] });
      queryClient.invalidateQueries({ queryKey: ["flagged-questions", user?.id, subject?.id] });
      queryClient.invalidateQueries({ queryKey: ["flagged-count", user?.id] });
      
      // Track flag/unflag
      if (variables.next) {
        trackQuestionFlagged({ questionId: variables.questionId, subjectSlug: slug! });
      } else {
        trackQuestionUnflagged({ questionId: variables.questionId, subjectSlug: slug! });
      }
    },
  });

  const total = questions.length;
  const q = questions[cursor];

  function accumulateTimeForCurrent() {
    if (!q) return;
    if (startTick == null) return;
    const delta = performance.now() - startTick;
    setTimings((t) => ({ ...t, [q.id]: (t[q.id] || 0) + delta }));
    setStartTick(performance.now());
  }

  function statusFor(id: string): AnswerStatus {
    return checked[id] ?? "unanswered";
  }

  function onCheck() {
    accumulateTimeForCurrent();
    if (!q) return;
    const chosen = selections[q.id];
    if (chosen === undefined) return;
    if (q.correctIndex == null) {
      toast({ title: "Answer unavailable", description: "This question doesn’t have an answer key yet." });
      return;
    }
    const st: AnswerStatus = chosen === q.correctIndex ? "correct" : "incorrect";
    setChecked((s) => ({ ...s, [q.id]: st }));    
    // Track question answered
    trackQuestionAnswered({
      questionId: q.id,
      subjectSlug: slug!,
      isCorrect: st === "correct",
      difficulty: q.difficulty,
      timeSpentMs: timings[q.id] ?? 0,
    });  }

  async function finish() {
    if (!sessionId) {
      toast({ title: "Session not ready", description: "Please wait for the questions to finish loading." });
      return;
    }
    if (!user) {
      toast({ title: "Sign in required", description: "Sign in to save your practice session." });
      return;
    }
    accumulateTimeForCurrent();

    const items = questions.map((question) => {
      const selectedIndex = selections[question.id];
      const elapsedMs = timings[question.id] ?? 0;
      const correct =
        selectedIndex === undefined || question.correctIndex == null
          ? null
          : selectedIndex === question.correctIndex;
      return {
        question,
        selectedIndex: selectedIndex ?? null,
        elapsedMs,
        correct,
      };
    });

    const correctCount = items.filter((item) => item.correct === true).length;

    const attemptsPayload = items.map((item) => ({
      questionId: item.question.id,
      selectedIndex: item.selectedIndex,
      isCorrect: item.correct,
      timeTakenSeconds: item.elapsedMs ? Math.round(item.elapsedMs / 1000) : null,
    }));

    try {
      setFinishing(true);
      await recordQuestionAttempts(sessionId, user.id, attemptsPayload);
      const percent = items.length ? Math.round((correctCount / items.length) * 100) : null;
      await completePracticeSession(sessionId, percent);
      queryClient.invalidateQueries({ queryKey: ["answered-question-ids", user.id, subject?.id] });
      queryClient.invalidateQueries({ queryKey: ["answered-questions", user.id, subject?.id] });
      queryClient.invalidateQueries({ queryKey: ["practice-history-user", user.id] });
      queryClient.invalidateQueries({ queryKey: ["practice-history", user.id, subject?.id] });
      // Clear saved draft after successful completion.
      if (draftKey) {
        try {
          localStorage.removeItem(draftKey);
        } catch {
          // ignore
        }
      }
      navigate(`/practice/${slug}/summary?id=${encodeURIComponent(sessionId)}`);
    } catch (error: unknown) {
      console.error(error);
      const message = error instanceof Error ? error.message : "An unexpected error occurred while saving your results.";
      toast({
        title: "Failed to save session",
        description: message,
        variant: "destructive",
      });
    } finally {
      setFinishing(false);
    }
  }

  const letter = (i: number) => String.fromCharCode(65 + i);

  function topicShort(title: string | null | undefined) {
    if (!title) return "All Topics";
    const match = title.match(/Topic\s+(\d+\.\d+)/);
    return match ? `Topic ${match[1]}` : title;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="border">
            <CardHeader>
              <CardTitle className="text-xl">Practice sessions</CardTitle>
              <CardDescription>Sign in to build custom practice sets and track your performance.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild>
                <Link to="/auth">Sign in</Link>
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (outlineQuery.isLoading || questionsQuery.isLoading || createSessionMutation.isPending) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="border">
            <CardHeader>
              <CardTitle className="text-xl">Loading practice set…</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-32 rounded-lg bg-muted/40 animate-pulse" />
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  if (questionsQuery.isError) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="border">
            <CardHeader>
              <CardTitle>Unable to load questions</CardTitle>
              <CardDescription>Try adjusting your filters or come back later.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate(`/practice/${slug}/setup`)}>Go back</Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const noQuestions = total === 0;
  const currentUnit = q ? unitLookup.get(q.unitId) : unitLookup.get(effectiveUnitIds[0]);
  const unitTitle = (currentUnit?.name ?? (currentUnit as { title?: string })?.title ?? currentUnit?.slug ?? "Unit") as string;
  const currentTopicTitle = q?.topicTitle ?? (q?.topicSlug ? topicLookup.get(q.topicSlug)?.title ?? q.topicSlug : "All Topics");

  const canPrev = cursor > 0;
  const canNext = cursor < total - 1;

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-md" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <Badge variant="secondary" className="ml-1">Practice Test</Badge>
          </div>
        </div>

        {noQuestions ? (
          <Card className="border">
            <CardHeader>
              <CardTitle>No questions available</CardTitle>
              <CardDescription>
                Try changing your filters. It's possible all matching questions have been completed and excluded.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate(`/practice/${slug}/setup`)}>Adjust settings</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
            <aside className="lg:sticky lg:top-20 h-max">
              <Card className="border bg-gradient-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Questions</CardTitle>
                  <CardDescription>{unitTitle} ({topicShort(currentTopicTitle)})</CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <ScrollArea className="h-[480px]">
                    <ol className="space-y-1 pr-2">
                      {questions.map((question, index) => {
                        const state = statusFor(question.id);
                        const active = index === cursor;
                        return (
                          <li key={question.id}>
                            <button
                              onClick={() => setCursor(index)}
                              className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left transition-colors ${
                                active ? "bg-muted" : "hover:bg-muted/70"
                              }`}
                            >
                              <span>Q{index + 1}</span>
                              <span
                                className={`h-2.5 w-2.5 rounded-full ${
                                  state === "unanswered"
                                    ? "bg-muted-foreground/30"
                                    : state === "correct"
                                    ? "bg-emerald-500"
                                    : "bg-rose-500"
                                }`}
                              />
                            </button>
                          </li>
                        );
                      })}
                    </ol>
                  </ScrollArea>
                </CardContent>
              </Card>
            </aside>

            <section className="space-y-4">
              <Card className="border">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="secondary"
                        size="icon"
                        className="rounded-md"
                        disabled={!canPrev}
                        onClick={() => setCursor((c) => Math.max(0, c - 1))}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <div className="text-sm font-medium">Question {cursor + 1} of {total}</div>
                      {q && (
                        <>
                          <Badge variant="secondary" className="ml-1">{topicShort(currentTopicTitle)}</Badge>
                          {q.difficulty !== "unassigned" && (
                            <span
                              className={`px-2 py-0.5 rounded text-xs font-medium ${
                                q.difficulty === "easy"
                                  ? "bg-emerald-100 text-emerald-700"
                                  : q.difficulty === "medium"
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-rose-100 text-rose-700"
                              }`}
                            >
                              {DIFFICULTY_LABELS[q.difficulty] ?? q.difficulty}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    <Button
                      variant="secondary"
                      size="icon"
                      className="rounded-md"
                      disabled={!canNext}
                      onClick={() => setCursor((c) => Math.min(total - 1, c + 1))}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {q ? (
                    <>
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        {q.kcRef && <><span>This question tests: {q.kcRef}</span><Info className="h-3.5 w-3.5" /></>}
                      </div>
                      <div className="text-base"><LatexText text={q.stem} /></div>
                      <div className="space-y-2">
                        {q.choices.map((choice, index) => {
                          const selected = selections[q.id] === index;
                          const state = statusFor(q.id);
                          const isCorrectChoice = state !== "unanswered" && q.correctIndex != null && index === q.correctIndex;
                          const isIncorrectSelected = state === "incorrect" && selected && (q.correctIndex == null || index !== q.correctIndex);
                          const isEliminated = Boolean(eliminated[q.id]?.has(index));
                          const border = isCorrectChoice
                            ? "border-emerald-500 bg-emerald-50"
                            : isIncorrectSelected
                            ? "border-rose-500 bg-rose-50"
                            : selected
                            ? "border-primary bg-primary/5"
                            : "border-muted";
                          return (
                            <div key={index} className="space-y-1">
                              <label
                                className={`flex items-center justify-between rounded-md border ${border} px-3 py-2 cursor-pointer ${
                                  isEliminated && state === "unanswered" && !selected ? "opacity-70" : ""
                                }`}
                                onClick={(event) => {
                                  event.preventDefault();
                                  if (eliminationMode) {
                                    setEliminated((prev) => {
                                      const set = new Set(prev[q.id] || []);
                                      if (set.has(index)) set.delete(index);
                                      else set.add(index);
                                      return { ...prev, [q.id]: set };
                                    });
                                  } else {
                                    setEliminated((prev) => {
                                      const curr = prev[q.id];
                                      if (curr && curr.has(index)) {
                                        const next = new Set(curr);
                                        next.delete(index);
                                        return { ...prev, [q.id]: next };
                                      }
                                      return prev;
                                    });
                                    setSelections((s) => ({ ...s, [q.id]: index }));
                                  }
                                }}
                              >
                                <div className="flex items-center gap-3">
                                  <div
                                    className={`h-8 w-8 rounded-full flex items-center justify-center border ${
                                      selected ? "border-primary text-primary" : "border-muted-foreground/30 text-muted-foreground"
                                    } bg-background`}
                                  >
                                    {letter(index)}
                                  </div>
                                  <div
                                    className={`text-sm ${
                                      isEliminated && state === "unanswered" && !selected ? "line-through text-muted-foreground" : ""
                                    }`}
                                  >
                                    <LatexText text={choice.text} />
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {state !== "unanswered" && isCorrectChoice && <Check className="h-5 w-5 text-emerald-600" />}
                                  {state === "incorrect" && isIncorrectSelected && <X className="h-5 w-5 text-rose-600" />}
                                </div>
                              </label>
                              {state !== "unanswered" && choice.reason && (
                                <div className="pl-14 pr-3 pb-2">
                                  <div className="text-xs text-muted-foreground italic">
                                    <LatexText text={choice.reason} />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex items-center justify-between pt-2">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            onClick={() => {
                              if (!q) return;
                              const next = !flagged[q.id];
                              setFlagged((f) => ({ ...f, [q.id]: next }));
                              toggleFlagMutation.mutate({ questionId: q.id, next });
                            }}
                            aria-pressed={q ? !!flagged[q.id] : false}
                            className="rounded-md"
                          >
                            <Flag className={`h-4 w-4 mr-2 ${q && flagged[q.id] ? "text-red-600" : "text-muted-foreground"}`} />
                            {q && flagged[q.id] ? "Flagged" : "Flag"}
                          </Button>
                          <Button
                            variant={eliminationMode ? "default" : "outline"}
                            onClick={() => setEliminationMode((mode) => !mode)}
                            className="rounded-md"
                          >
                            <Eraser className="h-4 w-4 mr-2" />
                            {eliminationMode ? "Eliminate: ON" : "Eliminate"}
                          </Button>
                          <span className="text-xs text-muted-foreground hidden sm:inline">Click a choice to strike it out</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" disabled={!canPrev} onClick={() => setCursor((c) => Math.max(0, c - 1))}>
                            <ChevronLeft className="h-4 w-4 mr-1" /> Prev
                          </Button>
                          <Button onClick={onCheck}>Check Answer</Button>
                          <Button variant="outline" disabled={!canNext} onClick={() => setCursor((c) => Math.min(total - 1, c + 1))}>
                            Next <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-muted-foreground">Select a question from the left.</div>
                  )}
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button onClick={finish} disabled={finishing}>
                  {finishing ? "Saving…" : "Finish and view summary"}
                </Button>
              </div>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
